import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useState } from 'react'
import { Combobox, ComboboxProps, makeStyles, mergeClasses, Option, OptionGroup, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { OptionOnSelectData, SelectionEvents } from '@fluentui/react-combobox'

const useStyles = makeStyles({
    root: { minWidth: '98px' },
    input: { minWidth: '50px' },
    listbox: { maxHeight: '400px' },
    group: {
        '> span': {
            color: tokens.colorBrandForeground1,
        },
    },
    subGroup: {
        paddingLeft: tokens.spacingHorizontalXL,
        ':not(:last-child)::after': {
            borderBottom: 'unset',
        },
    },
})

export type BaseComboboxProps<E, G = E> = Omit<ComboboxProps, 'value' | 'placeholder' | 'onOptionSelect'> & {
    entities: E[]
    onOptionSelect: (event: SelectionEvents, data: OptionOnSelectData, selectedEntities: E[]) => void

    filter?: (entity: E) => boolean
    alphabetical?: boolean
    sort?: (a: E, b: E) => number

    placeholderPrefixKey?: string
    placeholderKey?: string
    /** Appended to the placeholder this combobox builds itself, for instance to mark it as required. */
    placeholderSuffix?: string

    isGroup?: (entity: E) => boolean
    isSelectableGroup?: (entity: E) => boolean
    getGroupEntities?: (group: G) => E[]
    getGroupLabel?: (entity: G) => string // A short-form label.
    getGroupValue?: (entity: G) => string // The underlying value.

    isEntityDisabled?: (entity: E) => boolean
    getEntityShortLabel?: (entity: E) => string // A short-form label.
    getEntityLabel: (entity: E) => string // A long-form label.
    getEntityValue: (entity: E) => string // The underlying value.
    getEntitySearchValue?: (entity: E) => string // The value with which entities are filtered by.

    componentRef?: RefObject<HTMLInputElement>
}

type BaseOptionGroupProps<E, G> = BaseComboboxProps<E, G> & {
    index: string
    entity: any
    indentation: number
    searchValue?: string
    groupEntities?: E[]
}

export type SubComboboxProps<E, G = E> = Partial<BaseComboboxProps<E, G>> &
    Required<Pick<BaseComboboxProps<E, G>, 'selectedOptions' | 'onOptionSelect'>>

/**
 * Children run through `props.filter` here too (HAM-4313). Rendering options already filters at every level, so
 * without it the box could display a selection the list itself never offers: a folder picker preset on a document
 * showed that document's file name where a folder belongs.
 */
function findEntities<E, G>(entities: E[], selectedEntities: string[], props: BaseComboboxProps<E, G>): E[] {
    return entities
        .filter(entity => props.filter?.(entity) ?? true)
        .map(e =>
            props.isGroup?.(e)
                ? [...(props.isSelectableGroup?.(e) ? [e] : []), ...findEntities(props.getGroupEntities?.(e as any) || [], selectedEntities, props)]
                : [e],
        )
        .reduce((a, b) => a.concat(b), [])
        .filter(entity => selectedEntities.indexOf(props.getEntityValue(entity)) >= 0)
}

function getSelectionString<E, G>(selectedEntities: E[], props: BaseComboboxProps<E, G>): string {
    return selectedEntities
        .map(entity => props.getEntityShortLabel?.(entity) || props.getEntityLabel(entity))
        .filter(selection => selection)
        .join(', ')
}

function isEntityMatch<E, G>(entity: E, value: string, props: BaseComboboxProps<E, G>): boolean {
    if (!value || value?.trim().length === 0) {
        return true
    }

    const groupMatch = props.isGroup?.(entity) && props.getGroupEntities?.(entity as any).some(child => isEntityMatch(child, value, props))
    const entityMatch =
        props.getEntitySearchValue?.(entity)?.toLowerCase().indexOf(value.toLowerCase()) >= 0 ||
        props.getEntityLabel(entity).toLowerCase().indexOf(value.toLowerCase()) >= 0

    return groupMatch || entityMatch
}

function BaseOptionGroupClassic<E, G>(props: BaseOptionGroupProps<E, G>): ReactElement {
    const styles = useStyles()

    return (
        <OptionGroup label={props.getGroupLabel?.(props.entity)} className={mergeClasses(styles.group, props.indentation > 0 && styles.subGroup)}>
            {props.groupEntities
                ?.filter(child => isEntityMatch(child, props.searchValue, props))
                ?.map((child: E, childIndex: number) => {
                    if (props.isGroup?.(child)) {
                        return (
                            <BaseOptionGroup<E, G>
                                {...props}
                                key={`${props.index}-${childIndex}`}
                                entity={child}
                                indentation={props.indentation + 1}
                            />
                        )
                    } else {
                        return <BaseOption<E, G> {...props} key={`${props.index}-${childIndex}`} entity={child} indentation={props.indentation + 1} />
                    }
                })}
        </OptionGroup>
    )
}

function BaseOptionGroupSelectable<E, G>(props: BaseOptionGroupProps<E, G>): ReactElement {
    return (
        <>
            <BaseOption<E, G> {...props} key={`${props.index}`} entity={props.entity} />
            {props.groupEntities
                ?.filter(child => isEntityMatch(child, props.searchValue, props))
                ?.map((child: E, childIndex: number) => {
                    if (props.isGroup?.(child)) {
                        return (
                            <BaseOptionGroup<E, G>
                                {...props}
                                key={`${props.index}-${childIndex}`}
                                entity={child}
                                indentation={props.indentation + 1}
                            />
                        )
                    } else {
                        return <BaseOption<E, G> {...props} key={`${props.index}-${childIndex}`} entity={child} indentation={props.indentation + 1} />
                    }
                })}
        </>
    )
}

function BaseOptionGroup<E, G>(props: BaseOptionGroupProps<E, G>): ReactElement {
    const groupEntities = useMemo<E[]>(
        () => (props.isGroup?.(props.entity) ? props.getGroupEntities(props.entity).filter(entity => props.filter?.(entity) ?? true) : []),
        [props.entity, props.filter],
    )

    if (props.isGroup?.(props.entity)) {
        if (props.isSelectableGroup?.(props.entity)) {
            return <BaseOptionGroupSelectable {...props} groupEntities={groupEntities} />
        } else {
            return <BaseOptionGroupClassic {...props} groupEntities={groupEntities} />
        }
    } else {
        return <BaseOption<E, G> {...props} key={`${props.index}`} entity={props.entity} />
    }
}

function BaseOption<E, G>(props: BaseOptionGroupProps<E, G>): ReactElement {
    return (
        <Option
            value={props.getEntityValue(props.entity)}
            disabled={props.disabled || props.readOnly || props.isEntityDisabled?.(props.entity)}
            style={{ marginLeft: `calc(var(--spacingHorizontalM) * ${props.indentation})` }}
        >
            {props.getEntityLabel(props.entity)}
        </Option>
    )
}

export default function BaseCombobox<E, G = E>(props: BaseComboboxProps<E, G>): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const placeholder: string = useMemo(() => {
        const prefix = props.placeholderPrefixKey === '' ? '' : translate(props.placeholderPrefixKey ?? 'base.tagpicker.placeholder.prefix')
        const label =
            props.placeholderKey === ''
                ? ''
                : translate(props.placeholderKey ?? 'base.tagpicker.placeholder.option', {
                      count: props.multiselect ? 100 : 1,
                  })

        return [`${prefix} ${label}`.trim(), props.placeholderSuffix].filter(Boolean).join(' ')
    }, [props.placeholderPrefixKey, props.placeholderKey, props.placeholderSuffix])

    const entities: E[] = useMemo(
        () =>
            (props.entities || [])
                .filter(entity => props.filter?.(entity) ?? true)
                .sort((a, b) =>
                    props.alphabetical ? props.getEntityLabel(a).localeCompare(props.getEntityLabel(b)) : props.sort ? props.sort(a, b) : 0,
                ),
        [props.entities, props.filter, props.alphabetical, props.sort],
    )
    const selectedEntities: E[] = useMemo(() => findEntities(entities, props.selectedOptions || [], props), [props.selectedOptions, entities])

    const [matchingEntities, setMatchingEntities] = useState(entities)
    const [currentValue, setCurrentValue] = useState<string>('')
    const [searchValue, setSearchValue] = useState<string>('')

    useEffect(() => {
        setCurrentValue(getSelectionString(selectedEntities, props))
        setSearchValue(undefined)
        setMatchingEntities(entities)
    }, [selectedEntities, entities])

    const onChange: ComboboxProps['onChange'] = event => {
        const value = event.target.value.trim()
        const matches = entities.filter(entity => isEntityMatch(entity, value, props))
        setCurrentValue(event.target.value)
        setSearchValue(value)
        setMatchingEntities(matches)
    }

    return (
        <Combobox
            freeform
            //clearable is not available in multiselect mode
            clearable={!props.multiselect && !!currentValue && !props.required}
            placeholder={placeholder}
            autoComplete="off"
            className={styles.root}
            listbox={{ className: styles.listbox }}
            input={{ className: styles.input }}
            {...props}
            onChange={onChange}
            value={currentValue}
            onOptionSelect={(event, data) => {
                if (event.type === 'change') {
                    return
                }

                const curSelectedEntities = findEntities(entities, data.selectedOptions, props)

                // update selectedOptions
                props.onOptionSelect?.(event, data, curSelectedEntities)
                // set input value to selected entities
                setCurrentValue(getSelectionString(curSelectedEntities, props))
                setSearchValue(undefined)
                setMatchingEntities(entities)
            }}
            // update value to selected options on blur
            onBlur={() => {
                setCurrentValue(getSelectionString(selectedEntities, props))
                setSearchValue(undefined)
            }}
            ref={props.componentRef}
        >
            {matchingEntities.length >= 0 &&
                matchingEntities.map((entity: E, index: number) => {
                    return (
                        <BaseOptionGroup<E, G> {...props} key={index} index={`${index}`} entity={entity} indentation={0} searchValue={searchValue} />
                    )
                })}
            {matchingEntities.length === 0 && (
                <Option key="no-results" text="">
                    {translate('base.empty')}
                </Option>
            )}
        </Combobox>
    )
}
